const LOWERCASE_REGEXP = /[a-z]/;
const UPPERCASE_REGEXP = /[A-Z]/;
const DIGIT_REGEXP = /[0-9]/;
const SPECIALCHARS_REGEXP = /[^a-zA-Z0-9]/;
const MIN8CHARS_REGEXP = /.{8,}/;
const MIN10CHARS_REGEXP = /.{10,}/;
const BETWEENCHARS_REGEXP = /.{8,10}/;

const rules = {
  less8chars: /^.{0,7}&/,
  min8chars: MIN8CHARS_REGEXP,
  lower: LOWERCASE_REGEXP,
  upper: UPPERCASE_REGEXP,
  digit: DIGIT_REGEXP,
  special: SPECIALCHARS_REGEXP,
  betweenchars: BETWEENCHARS_REGEXP,
  min10chars: MIN10CHARS_REGEXP,
};

/**
 * @typedef {keyof typeof rules} TRuleKeys
 * @typedef {Record<'poor'|'weak'|'good'|'strong', { complete:boolean, rules: Partial<Record<TRuleKeys, boolean>> }>} TRuleGroups
 * @param {string?=} password
 * @typedef {{
 *  result: (keyof TRuleGroups)[],
 *  possibleMatch: keyof TRuleGroups,
 *  ruleGroups: TRuleGroups,
 *  completionList: Record<TRuleKeys, boolean>,
 * }} TInfo
 * @returns {TInfo}
 */
export default function getPasswordStrength(password = '') {
  /** @constant
  * @type {Record<TRuleKeys, boolean>}
  * */
  const completionList = Object
    .entries(rules)
    .reduce((result, [ruleName, rule]) => ({ ...result, [ruleName]: !!password?.match(rule) }), (/** @type {Record<TRuleKeys, boolean>} */({})));
  /** @constant
  * @type {TInfo}
  * */
  const info = {
    result: [],
    possibleMatch: 'poor',
    completionList,
    ruleGroups: {
      poor: {
        complete: false,
        rules: {
          less8chars: completionList.less8chars,
        },
      },
      weak: {
        complete: false,
        rules: {
          min8chars: completionList.min8chars,
        },
      },
      good: {
        complete: false,
        rules: {
          min8chars: completionList.min8chars,
          lower: completionList.lower,
          upper: completionList.upper,
          digit: completionList.digit,
          special: completionList.special,
          betweenchars: completionList.betweenchars,
        },
      },
      strong: {
        complete: false,
        rules: {
          min10chars: completionList.min10chars,
          lower: completionList.lower,
          upper: completionList.upper,
          digit: completionList.digit,
          special: completionList.special,
          betweenchars: completionList.betweenchars,
        },
      },
    },
  };

  info.result = (/** @type {(keyof TRuleGroups)[]} */(Object.entries(info.ruleGroups)
    .map(([groupName, groupRules]) => {
      // update group
      groupRules.complete = Object.values(groupRules.rules).every(Boolean);
      return groupRules.complete ? groupName : null
    })
    .filter(Boolean)));

  info.possibleMatch = info.result.at(-1) ?? 'poor';

  return info;
}
